import {
  Collection,
  fetchCollectionGuidesUpdate,
  guidesInfiniteQueryOptions,
} from '@blesk-frontend/api';
import {
  Button,
  Checkbox,
  Close,
  cn,
  Input,
  Loader,
  Modal,
  ModalProps,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@blesk-frontend/ui';
import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {debounce} from 'es-toolkit';
import {EllipsisVertical} from 'lucide-react';
import {Reorder, useInView} from 'motion/react';
import {ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';

interface MenuItemProps {
  title: ReactNode;
  before?: ReactNode;
  after?: ReactNode;
}

const MenuItem = ({title, before, after}: MenuItemProps) => {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: skip
    <label className="cursor-pointer">
      <div className="mr-8 flex h-12 items-center gap-4 rounded-lg border bg-bg-secondary px-4">
        <div className="size-5 shrink-0 text-text-secondary">{before}</div>
        <p className={cn('max-w-[304px] flex-1 truncate text-paragraph-sm text-text-primary')}>
          {title}
        </p>
        {after && <div className="size-6 shrink-0 text-text-secondary">{after}</div>}
      </div>
    </label>
  );
};

interface CollectionFillModalProps extends ModalProps {
  collection: Collection;
}

export const CollectionFillModal = ({collection, onClose, ...props}: CollectionFillModalProps) => {
  const [search, setSearch] = useState('');
  const onChangeQuery = useCallback(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
    }, 500),
    [],
  );

  const queryClient = useQueryClient();
  const {
    data: allGuides,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(guidesInfiniteQueryOptions({take: 10, sort: 'createdAt', filter: {search}}));

  const nextPageRef = useRef<HTMLDivElement>(null);
  const inView = useInView(nextPageRef);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const {
    register,
    setValue,
    handleSubmit,
    watch,
    formState: {isSubmitting},
  } = useForm<{
    guides: Record<string, boolean>;
    sorted: {id: string; name: string; position: number}[];
  }>({
    values: {
      guides: {},
      sorted:
        collection.guides.map((g) => ({
          id: g.guideId,
          position: g.position,
          name: g.guide.name,
        })) || [],
    },
  });

  const flatGuides = useMemo(() => {
    return allGuides?.pages.flatMap((page) => page.data) || [];
  }, [allGuides]);

  const selectedGuides = watch('sorted');

  useEffect(() => {
    const {unsubscribe} = watch((value, {name}) => {
      if (name?.includes('guides.')) {
        const sorted = Object.values(
          Object.entries(value.guides || {})
            .filter(([_key, value]) => value)
            .map(([key]) => key),
        )
          .map((id) => {
            const guide = flatGuides?.find((guide) => guide.id === id);
            const position =
              value.sorted?.find((guide) => guide?.id === id)?.position ??
              100 + (value.sorted?.length || 0);

            // biome-ignore lint/style/noNonNullAssertion: skip
            return {id: guide!.id, name: guide!.name, position};
          })
          .toSorted((a, b) => a.position - b.position);

        setValue('sorted', sorted);
      }
    });

    return () => unsubscribe();
  }, [flatGuides, watch, setValue]);

  const onRemoveSelectedGuide = (id: string) => {
    setValue(
      'sorted',
      selectedGuides.filter((guide) => guide.id !== id),
    );

    setValue(
      'guides',
      Object.fromEntries(Object.entries(watch('guides')).filter(([key]) => key !== id)),
    );
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!collection.id) return;
    await fetchCollectionGuidesUpdate(
      collection.id,
      data.sorted.map((guide) => ({guideId: guide.id, position: guide.position})),
    );
    await queryClient.refetchQueries({queryKey: ['collections', {id: collection.id}]});
    onClose?.();
  });

  return (
    <Modal onClose={onClose} {...props} closeOutside={false}>
      <h1 className="mb-6 text-title-h1">Собрать коллекцию</h1>
      <Tabs defaultValue="all">
        <TabsList className="mb-8 grid w-fit grid-cols-2">
          <TabsTrigger value="all">Все инструкции</TabsTrigger>
          <TabsTrigger
            value="selected"
            onClick={() => {
              setSearch('');
            }}
          >
            Выбранные
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <Input placeholder="Найти инструкцию" onChange={onChangeQuery} />

          <div className="-mx-8 mt-8 h-px bg-border-primary" />

          <div className="mr-[-30px] flex">
            <ScrollArea className="h-[350px] w-full" type="always">
              <div className="w-full">
                <div className="h-8" />
                {allGuides?.pages && !!allGuides?.pages.at(0)?.data.length ? (
                  <div className="flex flex-col gap-4 overflow-hidden">
                    {allGuides?.pages.map((page) =>
                      page.data.map((guide) => (
                        <MenuItem
                          key={`menu-item-${guide.id}`}
                          title={guide.name}
                          before={
                            <Checkbox
                              {...register(`guides.${guide.id}`)}
                              defaultChecked={selectedGuides.some((v) => v.id === guide.id)}
                              id={`instruction-${guide.id}`}
                              className="size-5"
                            />
                          }
                        />
                      )),
                    )}
                  </div>
                ) : (
                  !(isFetchingNextPage || isFetching) && (
                    <div className="flex h-[286px] w-full items-center justify-center pr-[30px]">
                      {!search ? 'Нет инструкций' : 'Ничего не найдено'}
                    </div>
                  )
                )}
                <div ref={nextPageRef} />
                {(isFetchingNextPage || isFetching) && (
                  <div className="mx-auto mt-4 w-fit pr-[30px] text-text-secondary">
                    <Loader size={24} />
                  </div>
                )}
                <div className="h-8" />
              </div>
            </ScrollArea>
          </div>
        </TabsContent>
        <TabsContent value="selected">
          <div className="-mx-8 mt-8 h-px bg-border-primary" />
          {selectedGuides && !!selectedGuides.length ? (
            <div className="mr-[-30px] flex">
              <ScrollArea className="h-[422px] w-full" type="always">
                <div className="h-8" />
                <Reorder.Group
                  axis="y"
                  values={selectedGuides}
                  onReorder={(values) => {
                    return setValue(
                      'sorted',
                      values
                        .map((v, i) => ({...v, position: i}))
                        .toSorted((a, b) => a.position - b.position),
                    );
                  }}
                  className="flex flex-col gap-4"
                >
                  {selectedGuides.map((value) => (
                    <Reorder.Item
                      layoutScroll={true}
                      key={`reorder-item-${value.id}`}
                      value={value}
                    >
                      <MenuItem
                        title={value.name}
                        before={<EllipsisVertical className="size-5" />}
                        after={
                          <Close
                            className="size-6 cursor-pointer"
                            onClick={() => {
                              onRemoveSelectedGuide(value.id);
                            }}
                          />
                        }
                      />
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
                <div className="h-8" />
              </ScrollArea>
            </div>
          ) : (
            <div className="flex h-[422px] w-full items-center justify-center">
              <div>Нет выбранных инструкций</div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="-mx-8 mb-8 h-px bg-border-primary" />

      <Button className="w-fit" onClick={onSubmit} loading={isSubmitting}>
        Сохранить
      </Button>
    </Modal>
  );
};
